import { StyleSpecification } from 'maplibre-gl';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { CustomizeBEFeature } from '@/endpoints/itasca/land/saved-selections/customize/getSavedSelectionFeatures';
import { SchoolcraftSoilResponse } from '@/endpoints/schoolcraft/soil/getSoil';
import {
  ICropHistoryData,
  ICropPercentages,
  useEmbedCrop,
} from '@/features/embed/useEmbedCrop';
import {
  IElevationData,
  useEmbedElevation,
} from '@/features/embed/useEmbedElevation';
import { useEmbedSoil } from '@/features/embed/useEmbedSoil';
import {
  EmbedSourceData,
  useEmbedSource,
} from '@/features/embed/useEmbedSource';
import { ReactSetState } from '@/models/reactTypes';
import { SoilIndex } from '@/models/soil/soil-type';
import { ISoilAverages } from '@/utils/uniqueEndpoints';

import {
  embedBaseMapStyleMap,
  EmbedMapStyle,
} from './mapSettingsComponents/embedBaseMap';

type Props = {
  children: ReactNode;
};

type EmbedContextType = EmbedSourceData & {
  activeCustomization: CustomizeBEFeature | null;
  setActiveCustomization: ReactSetState<CustomizeBEFeature | null>;
  hiddenCustomizations: CustomizeBEFeature[];
  hideCustomization: (cust: CustomizeBEFeature) => void;
  unhideCustomization: (cust: CustomizeBEFeature) => void;
  baseMap: EmbedMapStyle | null;
  setBaseMap: ReactSetState<EmbedMapStyle>;
  baseMapStyle: StyleSpecification | string | null;
  setBaseMapStyle: ReactSetState<Object | string | null>;
  soilData?: {
    activeSoilScoreIndex?: SoilIndex;
    soilAverages?: ISoilAverages;
    soilData: SchoolcraftSoilResponse | null;
  };
  getSoil: () => void;
  isSoilLoading: boolean;
  soilError: string | null;
  getCropHistory: () => void;
  cropError: string | null;
  cropHistoryData: ICropHistoryData | null;
  cropPercentages: ICropPercentages | null;
  isCropLoading: boolean;
  isElevationLoading: boolean;
  getElevation: () => void;
  elevationError: string | null;
  elevationData: IElevationData | null;
};

export const EmbedContext = React.createContext<EmbedContextType>({
  activeCustomization: null,
  baseMap: null,
  baseMapStyle: null,
  boundary: null,
  cropError: null,
  cropHistoryData: null,
  cropPercentages: null,
  elevationData: null,
  elevationError: null,
  getCropHistory: () => null,
  getElevation: () => {},
  getSoil: () => {},
  hiddenCustomizations: [],
  hideCustomization: () => {},
  isCropLoading: false,
  isElevationLoading: false,
  isSoilLoading: false,
  layers: [],
  originalBoundary: null,
  savedSelection: null,
  setActiveCustomization: () => {},
  setBaseMap: () => {},
  setBaseMapStyle: () => {},
  sharecode: null,
  soilData: undefined,
  soilError: null,
  unhideCustomization: () => {},
  zoomBoundary: null,
});

export const EmbedProvider: React.FC<Props> = ({ children }) => {
  const {
    originalBoundary,
    boundary,
    zoomBoundary,
    sharecode,
    layers,
    savedSelection,
  } = useEmbedSource();

  const {
    loading: isSoilLoading,
    soilData,
    error: soilError,
    fetchData: getSoil,
  } = useEmbedSoil(boundary);

  const {
    loading: isElevationLoading,
    elevationData,
    error: elevationError,
    fetchData: getElevation,
  } = useEmbedElevation(boundary);

  const {
    imagesLoading: isCropLoading,
    cropHistoryData,
    cropPercentages,
    error: cropError,
    fetchData: getCropHistory,
  } = useEmbedCrop(boundary);

  const [baseMap, setBaseMap] = useState<EmbedMapStyle>(
    EmbedMapStyle.Satellite
  );

  const [baseMapStyle, setBaseMapStyle] = useState<
    string | StyleSpecification | null
  >(embedBaseMapStyleMap[EmbedMapStyle.Satellite]);
  const [activeCustomization, setActiveCustomization] =
    useState<CustomizeBEFeature | null>(null);

  /** This keeps track of all of the customizations that the user has hidden on the embed */
  const [hiddenCustomizations, setHiddenCustomizations] = useState<
    CustomizeBEFeature[]
  >([]);

  /** Hide a customization from the embed */
  const hideCustomization = useCallback(
    (cust: CustomizeBEFeature) => {
      if (hiddenCustomizations.find((x) => x.id === cust.id)) {
        return;
      }
      setHiddenCustomizations((prev) => [...prev, cust]);
    },
    [hiddenCustomizations]
  );

  /** Unhide a customization from the embed */
  const unhideCustomization = useCallback((cust: CustomizeBEFeature) => {
    setHiddenCustomizations((prev) => prev.filter((x) => x.id !== cust.id));
  }, []);

  const value = useMemo(() => {
    return {
      activeCustomization,
      baseMap,
      baseMapStyle,
      boundary,
      cropError,
      cropHistoryData,
      cropPercentages,
      elevationData,
      elevationError,
      getCropHistory,
      getElevation,
      getSoil,
      hiddenCustomizations,
      hideCustomization,
      isCropLoading,
      isElevationLoading,
      isSoilLoading,
      layers,
      originalBoundary,
      savedSelection,
      setActiveCustomization,
      setBaseMap,
      setBaseMapStyle,
      sharecode,
      soilData,
      soilError,
      unhideCustomization,
      zoomBoundary,
    };
  }, [
    activeCustomization,
    baseMap,
    baseMapStyle,
    boundary,
    cropError,
    cropHistoryData,
    cropPercentages,
    elevationData,
    elevationError,
    getCropHistory,
    getElevation,
    getSoil,
    hiddenCustomizations,
    hideCustomization,
    isCropLoading,
    isElevationLoading,
    isSoilLoading,
    layers,
    originalBoundary,
    savedSelection,
    sharecode,
    soilData,
    soilError,
    unhideCustomization,
    zoomBoundary,
  ]);

  return (
    <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>
  );
};

export const useEmbedContext = () => useContext(EmbedContext);
